import * as React from 'react';
import { onlineManager } from '@tanstack/react-query';

export const ONLINE_RESTORED_VISIBLE_MS = 3000;

let queryOnlineManagerSetup = false;

export const isBrowserOnline = (): boolean => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return true;
  }

  return navigator.onLine;
};

export const setupQueryOnlineManager = (): void => {
  if (queryOnlineManagerSetup || typeof window === 'undefined') {
    return;
  }

  queryOnlineManagerSetup = true;
  onlineManager.setOnline(isBrowserOnline());
  onlineManager.setEventListener(setOnline => {
    const updateOnline = () => setOnline(true);
    const updateOffline = () => setOnline(false);

    window.addEventListener('online', updateOnline);
    window.addEventListener('offline', updateOffline);

    return () => {
      window.removeEventListener('online', updateOnline);
      window.removeEventListener('offline', updateOffline);
    };
  });
};

export const useNetworkStatus = () => {
  const [isOnline, setIsOnline] = React.useState(isBrowserOnline);
  const [showOnlineRestored, setShowOnlineRestored] = React.useState(false);
  const wasOfflineRef = React.useRef(!isBrowserOnline());
  const restoredTimerRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const clearRestoredTimer = () => {
      if (restoredTimerRef.current) {
        window.clearTimeout(restoredTimerRef.current);
        restoredTimerRef.current = null;
      }
    };

    const hideOnlineRestored = () => {
      setShowOnlineRestored(false);
      restoredTimerRef.current = null;
    };

    const handleOffline = () => {
      clearRestoredTimer();
      wasOfflineRef.current = true;
      setShowOnlineRestored(false);
      setIsOnline(false);
    };

    const handleOnline = () => {
      setIsOnline(true);

      if (wasOfflineRef.current) {
        setShowOnlineRestored(true);
        wasOfflineRef.current = false;
        clearRestoredTimer();
        restoredTimerRef.current = window.setTimeout(
          hideOnlineRestored,
          ONLINE_RESTORED_VISIBLE_MS
        );
      }
    };

    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);

    return () => {
      clearRestoredTimer();
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
    };
  }, []);

  return { isOnline, showOnlineRestored };
};
